/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Slide, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import QueryString from 'qs';
import { forwardRef, useEffect } from 'react';
import FormProvider from '../hook-form/form-provider';
import RHFTextField from '../hook-form/rhf-text-field';
import ControlledCheckbox from '../hook-form/check-box';
import { encryptRequest, decryptResponse } from '@/app/utilities/helpers/security';
import { axiosInstance } from '@/app/utilities/helpers/axios';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function AddXCPlayList({
  device,
  refresh,
  dialog,
  defaultV,
  selectedPlaylist,
  resetId,
  confirmed,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const Schema = Yup.object().shape({
    playlistName: Yup.string().required('Playlist name is required'),
    playlistHost: Yup.string().required('URL (Host name) is required'),
    userName: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
    isProtect: Yup.boolean(),
    pin: Yup.string().when('isProtect', {
      is: true,
      then: () =>
        Yup.string().required('Pin is required').max(6, 'Pin must be at maximum 6 characters'),
      otherwise: () => Yup.string().notRequired(),
    }),
    confirmPin: Yup.string().when('isProtect', {
      is: true,
      then: () =>
        Yup.string()
          .oneOf([Yup.ref('pin'), null], 'Passwords must match')
          .required('Confirm pin is required'),
      otherwise: () => Yup.string().notRequired(),
    }),
    checkPin: Yup.string(),
  });

  const defaultValues = {
    playlistName: '',
    playlistHost: '',
    userName: '',
    password: '',
    isProtect: false,
    pin: '',
    confirmPin: '',
  };

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = methods;

  const isProtect = watch('isProtect');

  const onSubmit = handleSubmit(async data => {
    try {
      if (selectedPlaylist?.pin && !confirmed.value) {
        if (data.checkPin === selectedPlaylist?.pin) {
          confirmed.onTrue();
          reset(defaultV);
        } else {
          enqueueSnackbar('Incorrect PIN', { variant: 'error' });
        }
      } else {
        const playlist_url = data.playlistHost;
        const parsedUrl = new URL(playlist_url);

        if (!parsedUrl) {
          enqueueSnackbar('Invalid URL', { variant: 'error' });
          dialog.onFalse();

          return;
        }

        const path = parsedUrl?.pathname;

        if (path !== '/') {
          enqueueSnackbar(
            'Host name should be the host that starts with http... and ending with port number or domain',
            { variant: 'error' }
          );
          dialog.onFalse();

          return;
        }

        const params = {};
        parsedUrl?.searchParams.forEach((value, key) => {
          params[key] = value;
        });

        if (Object.keys(params).length > 0) {
          enqueueSnackbar('Host name should not include query parameters', { variant: 'error' });
          dialog.onFalse();

          return;
        }

        const body = {
          data: encryptRequest({
            device_id: device.app_device_id,
            playlist_id: selectedPlaylist ? selectedPlaylist._id : -1,
            playlist_name: data.playlistName ?? '',
            playlist_url: data.playlistHost.trim().replace(/\s+/g, '') ?? '',
            protect: data.isProtect ? 1 : 0,
            pin: data.pin ?? '',
            playlist_type: 'xc',
            user_name: data.userName.trim().replace(/\s+/g, '') ?? '',
            password: data.password.trim().replace(/\s+/g, '') ?? '',
          }),
        };

        const response = await axiosInstance.post(
          'yVerTRPwCTyktXmAkgU8y3ZxpyscYqFV',
          QueryString.stringify(body)
        );

        const responseData = decryptResponse(response.data);

        if (responseData.success) {
          enqueueSnackbar(responseData.message, { variant: 'success' });
          reset(defaultValues);
          refresh();
        } else {
          enqueueSnackbar(responseData.message, { variant: 'error' });
        }
        dialog.onFalse();
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
      dialog.onFalse();
    }
  });

  useEffect(() => {
    reset(defaultV);
  }, [defaultV, reset]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => {
          dialog.onTrue();
          reset(defaultValues);
          resetId();
        }}
      >
        Add XC Playlist
      </Button>

      <Dialog
        keepMounted={false}
        open={dialog.value}
        onClose={dialog.onFalse}
        scroll="body"
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
      >
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <DialogTitle sx={{ pb: 2 }}>
            {selectedPlaylist?.pin && !confirmed.value ? 'Confirm Pin' : 'Manage XC Playlist'}
          </DialogTitle>
          <DialogContent>
            <Stack gap={3} marginTop={2}>
              {selectedPlaylist?.pin && !confirmed.value ? (
                <RHFTextField name="checkPin" label="Enter Pin *" type="text" />
              ) : (
                <>
                  <RHFTextField name="playlistName" label="Playlist Name *" type="text" />
                  <RHFTextField name="playlistHost" label="URL (Host name)" type="text" />
                  <RHFTextField name="userName" label="Username *" type="text" />
                  <RHFTextField name="password" label="Password *" type="text" />
                  <Stack gap={2}>
                    <Stack>
                      <ControlledCheckbox name="isProtect" label="Protect this playlist" />
                      <Alert severity="warning">
                        Protected playlists will not be viewed or modified without entering PIN
                      </Alert>
                    </Stack>
                    <Stack
                      gap={2}
                      sx={{
                        flexDirection: {
                          sm: 'column',
                          md: 'row',
                        },
                      }}
                    >
                      <RHFTextField name="pin" label="PIN" type="text" disabled={!isProtect} />

                      <RHFTextField
                        name="confirmPin"
                        label="Confirm PIN"
                        type="text"
                        disabled={!isProtect}
                      />
                    </Stack>
                  </Stack>
                </>
              )}

              <Stack gap={3} flexDirection="row" justifyContent="flex-end" marginBottom={2}>
                <Button onClick={dialog.onFalse}>Cancel</Button>
                <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          </DialogContent>
        </FormProvider>
      </Dialog>
    </>
  );
}
